import { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Building2, ChevronDown, ChevronRight, Info, ShieldAlert, Lock, Camera, X, Loader2,
  KeyRound, Pencil, Save, Plus, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { ServiceReconciliationPanel } from './ServiceReconciliationPanel';
import { ActualBagCounts } from './ActualBagCounts';
import PropertyAccessEntryMirror from '../PropertyAccessEntryMirror';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Iter-78 pt 10 — Canonical color palette (aligned with the backend
// /api/ops/properties/{id}/unit-statuses keys). Green pickup · Blue recycle
// · Yellow ready-to-collect · Orange no-placement · Red violation.
const COLOR_CLASSES = {
  green:  'bg-emerald-500/20 border-emerald-400 text-emerald-200',
  red:    'bg-red-500/25 border-red-500 text-red-100',
  yellow: 'bg-amber-400/25 border-amber-400 text-amber-100',
  blue:   'bg-blue-500/25 border-blue-500 text-blue-100',
  orange: 'bg-orange-500/25 border-orange-500 text-orange-100',
  gray:   'bg-zinc-900 border-zinc-700 text-zinc-300',
};
const COLOR_LABELS = {
  green:  'Trash pickup completed',
  red:    'Active violation',
  yellow: 'Trash ready to be collected',
  blue:   'Recycling ready to be collected',
  orange: 'No trash placement',
};

// Given a per-property { unit -> color } map and the buildings list,
// find the FIRST building (by index) that still has any orange unit.
// That building becomes the "locked" building — the agent must resolve
// every orange there before any other building becomes interactive.
const findLockedBuilding = (statusMap, buildings) => {
  for (const b of buildings || []) {
    const orange = (b.units || []).some((u) => ['yellow', 'blue'].includes(statusMap[`${b.building_number}:${u}`]));
    if (orange) return b.building_number;
  }
  return null;
};

export const PropertyStructureBoard = ({ properties: propsFromParent } = {}) => {
  const [properties, setProperties] = useState(propsFromParent || []);
  const [expanded, setExpanded] = useState(null);
  const [structures, setStructures] = useState({});
  const [unitStatuses, setUnitStatuses] = useState({});   // { property_id: {unit: color} }
  const [ledgerBuildings, setLedgerBuildings] = useState({});
  const [confirmTarget, setConfirmTarget] = useState(null); // { pid, building, unit }

  useEffect(() => {
    if (propsFromParent && propsFromParent.length) {
      setProperties(propsFromParent);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/field-service/properties`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          const props = data || [];
          setProperties(props.filter((p, i, a) => a.findIndex((x) => x.property_id === p.property_id) === i));
        }
      } catch (e) { console.error(e); }
    })();
  }, [propsFromParent]);

  const loadUnitStatuses = async (pid) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/field-service/${pid}`, { credentials: 'include' });
      if (res.ok) {
        const j = await res.json();
        const statuses = {};
        for (const building of j.buildings) for (const unit of building.units) statuses[`${building.building_number}:${unit.unit_number}`] = unit.color;
        setUnitStatuses((prev) => ({ ...prev, [pid]: statuses }));
        setLedgerBuildings((prev) => ({ ...prev, [pid]: j.buildings.map((b) => ({ building_number: b.building_number, units: b.units.map((u) => u.unit_number) })) }));
      }
    } catch (e) { /* silent */ }
  };

  const toggle = async (pid) => {
    if (expanded === pid) { setExpanded(null); return; }
    setExpanded(pid);
    if (!structures[pid]) {
      try {
        const res = await fetch(`${BACKEND_URL}/api/properties/${pid}/structure`, { credentials: 'include' });
        if (res.ok) {
          const s = await res.json();
          setStructures((prev) => ({ ...prev, [pid]: s }));
        }
      } catch (e) { console.error(e); }
    }
    loadUnitStatuses(pid);
  };

  const openConfirm = (pid, building, unit, color) => {
    if (!['yellow', 'blue'].includes(color)) return;
    setConfirmTarget({ pid, building, unit });
  };

  const onConfirmSubmitted = async (pid) => {
    setConfirmTarget(null);
    await loadUnitStatuses(pid);
  };

  useEffect(() => {
    const refresh = () => { if (expanded) loadUnitStatuses(expanded); };
    const timer = setInterval(refresh, 20000);
    window.addEventListener('necs:collections-changed', refresh);
    return () => { clearInterval(timer); window.removeEventListener('necs:collections-changed', refresh); };
  }, [expanded]);

  return (
    <Card className="bg-zinc-900 border-2 p-6 mb-2" style={{ borderColor: 'rgba(59, 130, 246, 0.5)' }} data-testid="property-structure-board">
      <h3 className="font-bold text-lg flex items-center text-white mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        <Building2 className="h-5 w-5 mr-2 text-blue-400" />
        PROPERTIES STRUCTURE BOARD
      </h3>
      <p className="text-xs text-zinc-400 mb-3">
        Exact layout of every community assigned to you — colors update in real-time from the trash-placement + violation feeds.
      </p>

      {/* Color legend */}
      <div
        className="flex flex-wrap gap-2 mb-4 rounded-md border border-zinc-800 bg-zinc-950/60 p-2"
        data-testid="structure-board-legend"
      >
        {(['green','red','yellow','blue','orange']).map((c) => (
          <div key={c} className="flex items-center gap-1.5" data-testid={`legend-${c}`}>
            <span className={`inline-block h-3 w-3 rounded-sm border ${COLOR_CLASSES[c]}`} />
            <span className="text-[10px] text-zinc-300">{COLOR_LABELS[c]}</span>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        {properties.length === 0 ? (
          <p className="text-xs text-zinc-500">No communities in your scope.</p>
        ) : properties.map((p) => {
          const open = expanded === p.property_id;
          const s = structures[p.property_id];
          const statusMap = unitStatuses[p.property_id] || {};
          const displayBuildings = ledgerBuildings[p.property_id] || s?.buildings || [];
          const lockedBuilding = s ? findLockedBuilding(statusMap, displayBuildings) : null;
          return (
            <div key={p.property_id} className="bg-zinc-800 border border-zinc-700 rounded-md" data-testid={`structure-row-${p.property_id}`}>
              <button
                type="button"
                onClick={() => toggle(p.property_id)}
                className="w-full flex items-center justify-between px-3 py-2 text-left"
                data-testid={`structure-toggle-${p.property_id}`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  {open ? <ChevronDown className="h-4 w-4 text-blue-400 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-zinc-500 flex-shrink-0" />}
                  <span className="text-xs font-bold text-white truncate">{p.property_name}</span>
                  <span className="text-[10px] text-zinc-500 truncate hidden sm:inline">{p.city || ''}</span>
                </span>
                <span className="flex items-center gap-1.5 flex-shrink-0">
                  {lockedBuilding && (
                    <Badge className="bg-orange-600 text-white text-[9px] uppercase" data-testid={`property-locked-${p.property_id}`}>
                      <Lock className="h-2.5 w-2.5 mr-0.5" /> Locked
                    </Badge>
                  )}
                  <Badge className="bg-zinc-700 text-zinc-200 text-[10px]">{p.total_units ?? '—'} units</Badge>
                </span>
              </button>

              {open && (
                <div className="px-3 pb-3">
                  {!s ? (
                    <p className="text-xs text-zinc-500">Loading structure…</p>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mb-2">
                        {[
                          ['Buildings', displayBuildings.length],
                          ['Trash Areas', s.trash_areas_count],
                          ['Pet Stations', s.pet_stations_count],
                          ['Dumpsters / Trash Rooms', s.dumpsters_count],
                        ].map(([label, value]) => (
                          <div key={label} className="bg-zinc-900 border border-zinc-700 rounded p-1.5 text-center">
                            <p className="text-sm font-black text-white leading-none">{value ?? '—'}</p>
                            <p className="text-[9px] uppercase tracking-wider text-zinc-500 mt-0.5">{label}</p>
                          </div>
                        ))}
                      </div>

                      <ServiceReconciliationPanel propertyId={p.property_id} />

                      {/* Iter-78 pt 11 · Iter-92 update — Field Entry
                          Logistics. Gate code always renders (N/A when
                          missing) so field staff never see a blank row.
                          Amenity list beneath shows each amenity + its
                          entry code (or N/A). Supervisors+ can edit; Site
                          Agents get read-only. */}
                      <PropertyAccessEntryMirror propertyId={p.property_id} readOnly />

                      {lockedBuilding && (
                        <div
                          className="mb-2 rounded-md border border-orange-500/60 bg-orange-500/10 p-2 flex items-start gap-2"
                          data-testid={`building-lock-banner-${p.property_id}`}
                        >
                          <Lock className="h-4 w-4 text-orange-300 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-[11px] font-bold text-orange-100">
                              Building {lockedBuilding} lock active
                            </p>
                            <p className="text-[10px] text-orange-200/80 leading-snug">
                              Trash or recycling is ready in this building. Service every yellow or blue unit before moving to the next building. Photo proof and actual bag counts are required for manual confirmation.
                            </p>
                          </div>
                        </div>
                      )}

                      {displayBuildings.map((b) => {
                        const bUnits = b.units || [];
                        const orangeInBuilding = bUnits.some((u) => ['yellow', 'blue'].includes(statusMap[`${b.building_number}:${u}`]));
                        const isLocked = lockedBuilding !== null && b.building_number !== lockedBuilding;
                        return (
                          <div
                            key={b.building_number}
                            className={`mb-1.5 ${isLocked ? 'opacity-40 grayscale pointer-events-none select-none' : ''}`}
                            data-testid={`building-row-${p.property_id}-${b.building_number}`}
                            data-locked={isLocked ? 'true' : 'false'}
                            aria-disabled={isLocked}
                          >
                            <p className="text-[11px] font-bold text-blue-300 mb-1 flex items-center gap-1 flex-wrap">
                              Building {b.building_number} <span className="text-zinc-500 font-normal">· {bUnits.length} units</span>
                              {/* Iter-78 pt 11 — Building entry code chip
                                  right beside the building name for every
                                  field-facing role. */}
                              {p.building_entry_codes?.[String(b.building_number)] && (
                                <span
                                  className="ml-1 inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-black border border-[#C9A962]/40 text-[#FFD60A]"
                                  title="Building entry code"
                                  data-testid={`building-entry-code-${p.property_id}-${b.building_number}`}
                                >
                                  🔑 {p.building_entry_codes[String(b.building_number)]}
                                </span>
                              )}
                              {orangeInBuilding && (
                                <span
                                  className="ml-1.5 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded text-orange-200 bg-orange-500/20 border border-orange-500/60"
                                title="Trash or recycling is ready — service this building before moving on."
                                  data-testid={`building-skip-hint-${p.property_id}-${b.building_number}`}
                                >
                                  <ShieldAlert className="h-2.5 w-2.5" />
                                  Priority pickup
                                </span>
                              )}
                              {isLocked && (
                                <span
                                  className="ml-1.5 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded text-zinc-300 bg-zinc-700 border border-zinc-600"
                                  data-testid={`building-locked-badge-${p.property_id}-${b.building_number}`}
                                >
                                  <Lock className="h-2.5 w-2.5" /> Locked
                                </span>
                              )}
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {bUnits.map((u) => {
                                const key = String(u);
                                const color = statusMap[`${b.building_number}:${key}`] || 'gray';
                                const clickable = ['yellow', 'blue'].includes(color) && !isLocked;
                                return (
                                  <button
                                    key={u}
                                    type="button"
                                    onClick={() => clickable && openConfirm(p.property_id, b.building_number, key, color)}
                                    disabled={!clickable}
                                    className={`px-1.5 py-0.5 rounded border text-[10px] font-mono transition ${COLOR_CLASSES[color]} ${clickable ? 'cursor-pointer hover:brightness-110' : 'cursor-default'}`}
                                    title={
                                      isLocked
                                        ? `Building locked — resolve Building ${lockedBuilding} first.`
                                        : color === 'gray'
                                          ? `${u} — no status yet`
                                          : ['yellow', 'blue'].includes(color)
                                            ? `${u} — bags ready. Record actual bag counts and photo proof.`
                                            : `${u} — ${COLOR_LABELS[color]}`
                                    }
                                    data-testid={`unit-${p.property_id}-${b.building_number}-${u}`}
                                    data-color={color}
                                  >
                                    {u}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}

                      <p className="mt-2 text-[10px] text-zinc-500 flex items-center gap-1">
                        <Info className="h-3 w-3" /> Tap any yellow unit to upload a photo and manually confirm &mdash; that&apos;s the only way to release
                        a building lock.
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ManualConfirmDialog
        target={confirmTarget}
        onClose={() => setConfirmTarget(null)}
        onConfirmed={onConfirmSubmitted}
      />
    </Card>
  );
};

// Photo-required proof-of-pickup dialog. Used only for ORANGE units.
const ManualConfirmDialog = ({ target, onClose, onConfirmed }) => {
  const [photo, setPhoto] = useState(null);         // { data, name }
  const [notes, setNotes] = useState('');
  const [actual, setActual] = useState({ actual_trash_bags: '', actual_recycling_bags: '' });
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!target) { setPhoto(null); setNotes(''); setActual({ actual_trash_bags: '', actual_recycling_bags: '' }); }
  }, [target]);

  if (!target) return null;

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Photo must be under 8MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setPhoto({ data: reader.result, name: file.name });
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const submit = async () => {
    if (!photo?.data) { toast.error('A photo is required as proof.'); return; }
    if (actual.actual_trash_bags === '' || actual.actual_recycling_bags === '') { toast.error('Record actual bag counts.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/ops/properties/${target.pid}/units/${encodeURIComponent(target.unit)}/manual-confirm`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            photo: photo.data,
            building_number: String(target.building),
            notes,
            actual_trash_bags: Number(actual.actual_trash_bags),
            actual_recycling_bags: Number(actual.actual_recycling_bags),
          }),
        },
      );
      const j = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(j.detail || 'Failed to confirm');
        return;
      }
      toast.success(`Unit ${target.unit} confirmed — building lock lifting.`);
      onConfirmed(target.pid);
      window.dispatchEvent(new CustomEvent('necs:collections-changed'));
    } catch (_) {
      toast.error('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-900 border-orange-500/40 text-white w-[calc(100%-2rem)] max-h-[85dvh] overflow-y-auto" data-testid="manual-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-orange-400" />
            Confirm pickup — Unit {target.unit}
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs">
            Building {target.building}. Resident placed trash here &mdash; upload a photo of the collected trash / cleared spot as proof. Once
            saved, the unit turns green and the building lock is released. The crew does not return to a serviced building.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <ActualBagCounts value={actual} onChange={setActual} prefix="manual-confirm" />
          {photo ? (
            <div className="relative w-full">
              <img
                src={photo.data}
                alt={photo.name}
                className="w-full aspect-[4/3] object-contain rounded border border-zinc-700"
                data-testid="manual-confirm-preview"
              />
              <button
                type="button"
                onClick={() => setPhoto(null)}
                className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white rounded-full p-1"
                data-testid="manual-confirm-remove-photo"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <>
              <label
                htmlFor="mc-photo-input"
                className="flex flex-col items-center justify-center rounded border border-dashed border-orange-500/60 bg-orange-500/5 h-40 cursor-pointer hover:border-orange-400 text-orange-200"
                data-testid="manual-confirm-photo-picker"
              >
                <Camera className="h-6 w-6 mb-1" />
                <span className="text-xs">Tap to capture / choose photo</span>
                <span className="text-[10px] text-orange-300/70">Required proof</span>
              </label>
              <input
                id="mc-photo-input"
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={onPickFile}
                data-testid="manual-confirm-photo-input"
              />
            </>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-400">Notes (optional)</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g. bag was already at curb, collected 9:12pm"
              className="bg-zinc-950 border-zinc-700 text-zinc-100 text-xs"
              data-testid="manual-confirm-notes"
            />
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            data-testid="manual-confirm-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !photo || actual.actual_trash_bags === '' || actual.actual_recycling_bags === ''}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
            data-testid="manual-confirm-submit"
          >
            {submitting ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Confirming…</> : 'Confirm pickup & unlock'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// Iter-92 · Field Entry Logistics card — Gate Code at top, amenities
// with entry codes beneath. Site Agents see it read-only; supervisors
// and above can inline-edit both fields.
function FieldEntryLogisticsCard({ propertyId, gateCode, amenities, onSaved }) {
  const { user } = useAuth();
  const canEdit = ['supervisor', 'manager', 'regional_manager', 'operations_director', 'admin'].includes(user?.role);
  // Iter-93 · Audit log viewer is Supervisor+ and NOT the CEO
  // (company_manager). CEO sees summary stats only, never the raw log.
  const canSeeAudit = canEdit || user?.role === 'crew_leader';
  const [editing, setEditing] = useState(false);
  const [draftGate, setDraftGate] = useState(gateCode || '');
  const [draftAmenities, setDraftAmenities] = useState(amenities || []);
  const [saving, setSaving] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [auditRows, setAuditRows] = useState([]);

  useEffect(() => {
    setDraftGate(gateCode || '');
    setDraftAmenities(amenities || []);
  }, [gateCode, amenities]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/properties/${propertyId}/access-codes`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gate_code: (draftGate || '').trim(),
          amenities: draftAmenities.filter((a) => a.name.trim()).map((a) => ({ name: a.name.trim(), entry_code: (a.entry_code || '').trim() })),
        }),
      });
      if (res.ok) {
        toast.success('Access codes updated');
        onSaved?.({ gate_code: draftGate.trim(), amenities: draftAmenities });
        setEditing(false);
      } else {
        const j = await res.json().catch(() => ({}));
        toast.error(j.detail || 'Save failed');
      }
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const renderCode = (v) => (v && String(v).trim()) ? String(v).trim() : 'N/A';

  return (
    <div
      className="mb-2 rounded-md border border-[#C9A962]/50 bg-zinc-950 px-3 py-2"
      data-testid={`field-entry-logistics-${propertyId}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <KeyRound className="h-3.5 w-3.5 text-[#C9A962]" />
          <span className="text-[10px] uppercase tracking-widest text-[#C9A962] font-black">Field Entry Logistics</span>
        </div>
        {canEdit && !editing && (
          <div className="flex items-center gap-2">
            {canSeeAudit && (
              <button
                type="button"
                onClick={async () => {
                  const next = !showAudit;
                  setShowAudit(next);
                  if (next) {
                    try {
                      const r = await fetch(`${BACKEND_URL}/api/properties/${propertyId}/access-codes/audit`, { credentials: 'include' });
                      if (r.ok) setAuditRows((await r.json()).events || []);
                    } catch { /* silent */ }
                  }
                }}
                className="text-[10px] text-[#C9A962] hover:text-[#FFD60A] flex items-center gap-1"
                data-testid={`field-entry-audit-toggle-${propertyId}`}
              >
                📜 {showAudit ? 'Hide' : 'Audit log'}
              </button>
            )}
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-[10px] text-[#C9A962] hover:text-[#FFD60A] flex items-center gap-1"
              data-testid={`field-entry-edit-${propertyId}`}
            >
              <Pencil className="h-3 w-3" /> Edit
            </button>
          </div>
        )}
      </div>

      {!editing ? (
        <>
          <div className="flex items-center gap-2 mb-1.5" data-testid={`gate-code-row-${propertyId}`}>
            <span className="text-[11px] text-zinc-400 font-semibold min-w-[80px]">Gate Code:</span>
            <span className={`font-mono text-xs px-2 py-0.5 rounded border ${gateCode ? 'text-[#FFD60A] border-[#C9A962]/40 bg-black' : 'text-zinc-500 border-zinc-700 bg-zinc-900'}`}>
              {renderCode(gateCode)}
            </span>
          </div>
          {(amenities || []).length === 0 ? (
            <p className="text-[10px] text-zinc-500 italic">No amenities recorded — request from Ops Supervisor.</p>
          ) : (
            <div className="space-y-1">
              {amenities.map((a, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px]" data-testid={`amenity-row-${propertyId}-${i}`}>
                  <span className="text-zinc-300 min-w-[140px] truncate">{a.name}</span>
                  <span className="text-zinc-500">—</span>
                  <span className="text-zinc-400 text-[10px]">Entry Code:</span>
                  <span className={`font-mono text-xs px-2 py-0.5 rounded border ${a.entry_code ? 'text-[#FFD60A] border-[#C9A962]/40 bg-black' : 'text-zinc-500 border-zinc-700 bg-zinc-900'}`}>
                    {renderCode(a.entry_code)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="space-y-2" data-testid={`field-entry-edit-form-${propertyId}`}>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-zinc-400 font-semibold min-w-[80px]">Gate Code:</span>
            <Input
              value={draftGate}
              onChange={(e) => setDraftGate(e.target.value)}
              placeholder="N/A if none"
              className="bg-zinc-900 border-zinc-700 text-white text-xs h-7 max-w-[160px]"
              data-testid={`field-entry-gate-input-${propertyId}`}
            />
          </div>
          <div className="space-y-1">
            {draftAmenities.map((a, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <Input
                  value={a.name}
                  onChange={(e) => setDraftAmenities((s) => s.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                  placeholder="Amenity name"
                  className="bg-zinc-900 border-zinc-700 text-white text-xs h-7 flex-1"
                  data-testid={`field-entry-amenity-name-${propertyId}-${i}`}
                />
                <Input
                  value={a.entry_code || ''}
                  onChange={(e) => setDraftAmenities((s) => s.map((x, j) => j === i ? { ...x, entry_code: e.target.value } : x))}
                  placeholder="Code (or blank = N/A)"
                  className="bg-zinc-900 border-zinc-700 text-white text-xs h-7 w-[140px] font-mono"
                  data-testid={`field-entry-amenity-code-${propertyId}-${i}`}
                />
                <button type="button" onClick={() => setDraftAmenities((s) => s.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-300">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setDraftAmenities((s) => [...s, { name: '', entry_code: '' }])}
              className="text-[10px] text-[#C9A962] flex items-center gap-1 hover:text-[#FFD60A]"
              data-testid={`field-entry-add-amenity-${propertyId}`}
            >
              <Plus className="h-3 w-3" /> Add amenity
            </button>
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              size="sm"
              onClick={save}
              disabled={saving}
              className="h-7 px-3 text-xs font-bold"
              style={{ backgroundColor: '#C9A962', color: '#1a1a1a' }}
              data-testid={`field-entry-save-${propertyId}`}
            >
              <Save className="h-3 w-3 mr-1" /> {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => { setEditing(false); setDraftGate(gateCode || ''); setDraftAmenities(amenities || []); }}
              className="h-7 px-3 text-xs"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      {showAudit && (
        <div className="mt-2 border-t border-zinc-800 pt-2" data-testid={`field-entry-audit-list-${propertyId}`}>
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">
            Access Code Audit · masked
          </p>
          {auditRows.length === 0 ? (
            <p className="text-[10px] text-zinc-500 italic">No changes recorded yet.</p>
          ) : (
            <div className="space-y-0.5 max-h-32 overflow-y-auto">
              {auditRows.map((row) => (
                <div key={row.audit_id} className="flex items-center gap-2 text-[10px] py-0.5">
                  <span className="text-zinc-500 font-mono w-24 shrink-0">{(row.edited_at || '').slice(5, 16)}</span>
                  <span className="text-zinc-300 flex-1 truncate" title={row.field}>{row.field}</span>
                  <span className={`font-mono text-[9px] ${row.action === 'deleted' ? 'text-red-400' : row.action === 'created' ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {row.action}
                  </span>
                  <span className="font-mono text-[9px] text-zinc-500">{row.old_masked}</span>
                  <span className="text-zinc-500">→</span>
                  <span className="font-mono text-[9px] text-white">{row.new_masked}</span>
                  <span className="text-[9px] text-zinc-500 hidden md:inline">by {row.edited_by_name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
